import clsx from 'clsx';
import { forwardRef } from 'react';

import { ICountBadgeProps } from './types';

const testId = 'of-count-badge';

/**
 * A numeric count pip anchored to the top-right of its child — an unread
 * counter for an icon button.
 *
 * Distinct from `componentsv2/Badge`, which is a semantic *label* ("you", "new")
 * rendered inline after its children. This one is a positioned circular pip and
 * shares none of that styling, which is why it is its own primitive rather than
 * a mode of that one. `components/Layout/UserMenu` made the same split in v1.
 *
 * The pip is `aria-hidden`: it duplicates information that belongs in the
 * trigger's own accessible name, and a screen reader announcing "Notifications,
 * 3 unread" followed by a bare "3" is worse than not announcing it twice. Give
 * the child control an aria-label that includes the count.
 */
const CountBadge = forwardRef<HTMLSpanElement, ICountBadgeProps>(
  ({ count, max = 99, children, className, badgeClassName }, ref) => (
    <span ref={ref} className={clsx(className, 'relative inline-flex')} data-testid={testId}>
      {children}
      {count > 0 ? (
        <span
          aria-hidden
          data-testid={`${testId}-pip`}
          className={clsx(
            badgeClassName,
            'absolute right-[0] top-[0] inline-flex h-16 min-w-16 items-center',
            'justify-center rounded-full bg-status-error px-4 text-10 font-medium text-white'
          )}
        >
          {count > max ? `${max}+` : count}
        </span>
      ) : null}
    </span>
  )
);

CountBadge.displayName = 'CountBadge';

export { testId as CountBadgeTestId };
export type { ICountBadgeProps };
export default CountBadge;
